// frontend/src/components/pce/ConsumptionViewerpce.jsx
import React, { useState, useEffect } from 'react';
import { X, Calendar, BarChart3, Download, RefreshCw } from 'lucide-react';
import { fetchWithAuth } from '../../services/api';

const ConsumptionViewerpce = ({ pce, onClose }) => {
    const [consumption, setConsumption] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState({
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 jours
        end: new Date().toISOString().split('T')[0]
    });

    useEffect(() => {
        fetchConsumption();
    }, [dateRange]);

    const fetchConsumption = async () => {
        setLoading(true);
        try {
            const result = await fetchWithAuth(
                `/api/pce/${pce.id}/consumption?startDate=${dateRange.start}&endDate=${dateRange.end}`
            );
            if (result.success) {
                setConsumption(result.data);
            }
        } catch (error) {
            console.error('Erreur lors du chargement de la consommation:', error);
        } finally {
            setLoading(false);
        }
    };

    const calculateStats = () => {
        if (consumption.length === 0) return {};
        
        const totalConsumption = consumption.reduce((sum, record) => sum + (record.flow_m3 || 0), 0);
        const maxFlow = Math.max(...consumption.map(r => r.flow_m3 || 0));
        const avgFlow = totalConsumption / consumption.length;
        
        return {
            total: totalConsumption.toFixed(2),
            max: maxFlow.toFixed(2),
            average: avgFlow.toFixed(2),
            records: consumption.length
        };
    };

    const stats = calculateStats();

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b">
                    <div>
                        <h2 className="text-xl font-semibold">Données de consommation</h2>
                        <p className="text-gray-600">
                            {pce.site_name} - {pce.pce_number}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <div className="p-6">
                    {/* Contrôles */}
                    <div className="flex items-center gap-4 mb-6">
                        <div className="flex items-center gap-2">
                            <Calendar className="h-5 w-5 text-gray-400" />
                            <input
                                type="date"
                                value={dateRange.start}
                                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                                className="border border-gray-300 rounded px-3 py-2 text-sm"
                            />
                            <span className="text-gray-500">à</span>
                            <input
                                type="date"
                                value={dateRange.end}
                                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                                className="border border-gray-300 rounded px-3 py-2 text-sm"
                            />
                        </div>
                        
                        <button
                            onClick={fetchConsumption}
                            disabled={loading}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                        >
                            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                            Actualiser
                        </button>
                        
                        <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50">
                            <Download className="h-4 w-4" />
                            Exporter
                        </button>
                    </div>

                    {/* Statistiques */}
                    {!loading && consumption.length > 0 && (
                        <div className="grid grid-cols-4 gap-4 mb-6">
                            <div className="bg-green-50 p-4 rounded-lg">
                                <p className="text-sm text-green-600 font-medium">Consommation totale</p>
                                <p className="text-2xl font-bold text-green-900">{stats.total} m³</p>
                            </div>
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <p className="text-sm text-blue-600 font-medium">Débit max</p>
                                <p className="text-2xl font-bold text-blue-900">{stats.max} m³/h</p>
                            </div>
                            <div className="bg-yellow-50 p-4 rounded-lg">
                                <p className="text-sm text-yellow-600 font-medium">Moyenne</p>
                                <p className="text-2xl font-bold text-yellow-900">{stats.average} m³/h</p>
                            </div>
                            <div className="bg-purple-50 p-4 rounded-lg">
                                <p className="text-sm text-purple-600 font-medium">Points de données</p>
                                <p className="text-2xl font-bold text-purple-900">{stats.records}</p>
                            </div>
                        </div>
                    )}

                    {/* Contenu */}
                    <div className="bg-gray-50 rounded-lg p-4 h-96 overflow-auto">
                        {loading ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                            </div>
                        ) : consumption.length > 0 ? (
                            <div className="space-y-2">
                                <div className="grid grid-cols-4 gap-4 font-semibold text-sm text-gray-700 pb-2 border-b">
                                    <span>Horodatage</span>
                                    <span>Débit (m³/h)</span>
                                    <span>Période tarifaire</span>
                                    <span>Source</span>
                                </div>
                                {consumption.map((record, index) => (
                                    <div key={index} className="grid grid-cols-4 gap-4 text-sm py-2 hover:bg-white rounded">
                                        <span>{new Date(record.timestamp).toLocaleString('fr-FR')}</span>
                                        <span className="font-mono">{(record.flow_m3 || 0).toFixed(3)}</span>
                                        <span className="capitalize">{record.tariff_period || 'N/A'}</span>
                                        <span className="text-green-600">{record.source || 'grdf'}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-full text-gray-500">
                                <div className="text-center">
                                    <BarChart3 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                    <p className="font-medium">Aucune donnée disponible</p>
                                    <p className="text-sm">Aucune donnée de consommation pour cette période</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConsumptionViewerpce;